import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Scissors, TrendingDown, ShieldCheck, RefreshCw, Loader2, Zap, Check, Clock, Undo2, PiggyBank, ChevronDown } from 'lucide-react';
import { cn } from '../../lib/utils';
import { getOptimiser, getSavings, markSaving, unmarkSaving, CUR, CUR_FULL } from './api';
import { CountUp } from './useCountUp';
import { SavingsTimelineChart } from './CfoCharts';

const BUCKETS = {
  cut_completely: { title: 'Cut Completely', icon: Scissors, accent: 'text-rose-600', ring: 'border-rose-200', bar: 'bg-rose-400', glow: 'rgba(248,113,113,0.35)' },
  cut_back: { title: 'Cut Back', icon: TrendingDown, accent: 'text-amber-600', ring: 'border-amber-200', bar: 'bg-amber-400', glow: 'rgba(251,191,36,0.35)' },
  keep: { title: 'Keep · Working Well', icon: ShieldCheck, accent: 'text-emerald-600', ring: 'border-emerald-200', bar: 'bg-emerald-400', glow: 'rgba(52,211,153,0.35)' },
};
const CONF = { high: 'text-emerald-600', medium: 'text-amber-600', low: 'text-slate-400' };

const ItemCard = ({ it, cur, saved, onMark, onUndo, busy }) => {
  const st = BUCKETS[it.bucket];
  const range = it.suggested_min != null && it.suggested_max != null;
  const actioned = saved?.status === 'actioned';
  const canAct = it.bucket !== 'keep';
  return (
    <div className={cn('rounded-xl border bg-white p-3.5 transition-all', actioned ? 'border-emerald-300 bg-emerald-50/60' : st.ring)} data-testid={`opt-item-${it.merchant}`}>
      <div className="flex items-start justify-between gap-2 mb-1.5">
        <div className="min-w-0">
          <div className="text-sm font-extrabold text-slate-900 truncate flex items-center gap-1.5">{it.merchant}{actioned && <Check className="w-3.5 h-3.5 text-emerald-600 shrink-0" />}</div>
          <div className="text-[11px] text-slate-400">{it.category || '—'}</div>
        </div>
        {canAct && it.monthly_saving > 0 && (
          <div className="text-right shrink-0">
            <div className={cn('font-fin text-sm font-bold', st.accent)}>{CUR(it.monthly_saving, cur)}/mo</div>
            <div className="text-[10px] text-slate-400 font-fin">{CUR(it.annual_saving, cur)}/yr</div>
          </div>
        )}
      </div>
      <div className="flex items-center gap-2 text-[11px] font-fin mb-1.5">
        <span className="text-slate-500">Now <b className="text-slate-800">{CUR(it.current_monthly, cur)}/mo</b></span>
        {range && <span className="text-slate-400">→ target <b className={st.accent}>{CUR(it.suggested_min, cur)}–{CUR(it.suggested_max, cur)}</b></span>}
      </div>
      <p className="text-xs text-slate-500 leading-relaxed">{it.reason}</p>
      <div className="mt-2 flex items-center justify-between gap-2">
        <span className={cn('text-[10px] font-bold uppercase tracking-wider', CONF[it.confidence] || CONF.medium)}>{it.confidence} confidence</span>
        {canAct && (actioned ? (
          <button onClick={() => onUndo(it)} disabled={busy} className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-600 hover:text-emerald-700" data-testid={`opt-undo-${it.merchant}`}>
            <Undo2 className="w-3.5 h-3.5" /> Actioned — undo
          </button>
        ) : (
          <div className="flex items-center gap-1.5">
            <button onClick={() => onMark(it, 'snoozed')} disabled={busy} className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-500 hover:text-slate-900 px-2 py-1 rounded-md hover:bg-slate-100" data-testid={`opt-snooze-${it.merchant}`}>
              <Clock className="w-3.5 h-3.5" /> Snooze
            </button>
            <button onClick={() => onMark(it, 'actioned')} disabled={busy} className="inline-flex items-center gap-1 text-[11px] font-bold text-white bg-slate-900 hover:bg-slate-800 px-2.5 py-1 rounded-md" data-testid={`opt-action-${it.merchant}`}>
              <Check className="w-3.5 h-3.5" /> Mark actioned
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

const Column = ({ bucket, items, cur, savedMap, onMark, onUndo, busy }) => {
  const st = BUCKETS[bucket];
  const Icon = st.icon;
  const total = items.reduce((s, i) => s + (i.monthly_saving || 0), 0);
  return (
    <div className="flex flex-col" data-testid={`opt-col-${bucket}`}>
      <div className="flex items-center justify-between mb-2.5 px-0.5">
        <div className={cn('inline-flex items-center gap-1.5 text-[11px] font-extrabold uppercase tracking-widest', st.accent)}>
          <Icon className="w-4 h-4" /> {st.title} <span className="text-slate-400">· {items.length}</span>
        </div>
        {bucket !== 'keep' && total > 0 && <span className={cn('font-fin text-xs font-bold', st.accent)}>{CUR(total, cur)}/mo</span>}
      </div>
      <div className={cn('h-0.5 rounded-full mb-3', st.bar)} style={{ boxShadow: `0 0 8px ${st.glow}` }} />
      <div className="space-y-2.5">
        {items.length === 0 ? <div className="text-xs text-slate-400 text-center py-6 border border-dashed border-slate-200 rounded-xl">Nothing here</div>
          : items.map((it, i) => <ItemCard key={i} it={it} cur={cur} saved={savedMap[it.merchant.toLowerCase()]} onMark={onMark} onUndo={onUndo} busy={busy} />)}
      </div>
    </div>
  );
};

export const ExpenseOptimiser = ({ token, currency = 'AUD', reloadKey }) => {
  const [data, setData] = useState(null);
  const [savings, setSavings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [showSnoozed, setShowSnoozed] = useState(false);

  const loadSavings = useCallback(async () => {
    if (!token) return;
    try { setSavings(await getSavings(token)); } catch (e) { console.error(e); }
  }, [token]);

  const load = useCallback(async (refresh = false) => {
    if (!token) return;
    setLoading(true);
    try { const [o] = await Promise.all([getOptimiser(token, refresh), loadSavings()]); setData(o); } catch (e) { console.error(e); } finally { setLoading(false); }
  }, [token, loadSavings]);

  useEffect(() => { load(false); }, [load, reloadKey]);

  const cur = data?.currency || currency;
  const savedMap = {};
  (savings?.savings || []).forEach((s) => { savedMap[s.merchant_key] = s; });
  const statusOf = (it) => savedMap[it.merchant.toLowerCase()]?.status;

  const mark = async (it, status) => {
    setBusy(true);
    try {
      await markSaving(token, { merchant: it.merchant, category: it.category, bucket: it.bucket, monthly_saving: it.monthly_saving, annual_saving: it.annual_saving, reason: it.reason, status });
      toast.success(status === 'actioned' ? `Locked in ${CUR(it.monthly_saving, cur)}/mo` : 'Snoozed');
      await loadSavings();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const undo = async (it) => {
    setBusy(true);
    try { await unmarkSaving(token, it.merchant.toLowerCase()); await loadSavings(); } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const s = data?.summary;
  const byBucket = (b) => (data?.items || []).filter((i) => i.bucket === b && statusOf(i) !== 'snoozed');
  const snoozedItems = (data?.items || []).filter((i) => statusOf(i) === 'snoozed');
  const sv = savings?.summary;
  const identifiedMonthly = s?.total_monthly || 0;
  const lockedMonthly = sv?.locked_monthly || 0;
  const progress = identifiedMonthly + lockedMonthly > 0 ? Math.min(100, (lockedMonthly / (lockedMonthly + identifiedMonthly)) * 100) : 0;

  return (
    <section className="cfo-rise" style={{ animationDelay: '200ms' }} data-testid="cfo-optimiser">
      <div className="cfo-card p-5">
        <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
          <div>
            <h3 className="text-[13px] font-extrabold uppercase tracking-wide text-slate-700 flex items-center gap-1.5"><Zap className="w-4 h-4 text-lime-600" /> Expense Optimiser</h3>
            <p className="text-[11px] text-slate-400">DerwentAI finds what to cut, what to trim, and what's earning its keep — with the reasons why</p>
          </div>
          <button onClick={() => load(true)} disabled={loading} className="text-xs font-bold text-slate-500 hover:text-slate-900 inline-flex items-center gap-1" data-testid="opt-refresh">
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Re-analyse
          </button>
        </div>

        {/* Savings tracker */}
        {(lockedMonthly > 0 || identifiedMonthly > 0) && (
          <div className="rounded-2xl bg-gradient-to-r from-emerald-50 to-lime-50 border border-emerald-200 p-4 sm:p-5 mb-5" data-testid="cfo-savings-tracker">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <div className="text-[11px] font-bold uppercase tracking-widest text-emerald-700 flex items-center gap-1.5"><PiggyBank className="w-3.5 h-3.5" /> Savings locked in</div>
                <div className="font-fin text-3xl sm:text-4xl font-extrabold text-slate-900 leading-none mt-1" data-testid="savings-locked">
                  <CountUp value={lockedMonthly} render={(n) => CUR_FULL(Math.round(n))} /><span className="text-lg text-slate-400 font-bold">/mo</span>
                </div>
                <div className="text-[11px] text-slate-500 font-fin mt-1">{CUR(sv?.locked_annual || 0, cur)}/yr · {sv?.actioned_count || 0} actioned{sv?.snoozed_count ? ` · ${sv.snoozed_count} snoozed` : ''}</div>
              </div>
              <div className="sm:text-right">
                <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">Still on the table</div>
                <div className="font-fin text-lg font-bold text-lime-600">{CUR(identifiedMonthly, cur)}/mo</div>
              </div>
            </div>
            <div className="mt-3 h-2 rounded-full bg-white/70 border border-emerald-100 overflow-hidden">
              <div className="h-full rounded-full bg-emerald-500 transition-all" style={{ width: `${progress}%`, boxShadow: '0 0 8px rgba(16,185,129,0.5)' }} />
            </div>
            <div className="text-[10px] text-slate-500 mt-1 font-fin">{Math.round(progress)}% of identified savings captured</div>
            {(savings?.timeline || []).length >= 2 && (
              <div className="mt-4 pt-3 border-t border-emerald-100">
                <div className="text-[10px] font-bold uppercase tracking-widest text-emerald-700 mb-1">Momentum · cumulative savings locked in</div>
                <SavingsTimelineChart series={savings.timeline} currency={cur} />
              </div>
            )}
          </div>
        )}

        {loading && !data ? <div className="h-24 flex items-center justify-center text-lime-600"><Loader2 className="w-6 h-6 animate-spin" /></div>
          : !data?.has_data ? <div className="py-10 text-center text-sm text-slate-400">Add some expenses and I'll hunt for savings across every supplier.</div>
            : (data.items || []).length === 0 ? <div className="py-10 text-center text-sm text-slate-400">Nothing obvious to cut yet — your spending looks lean.</div>
              : (
                <>
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                    <Column bucket="cut_completely" items={byBucket('cut_completely')} cur={cur} savedMap={savedMap} onMark={mark} onUndo={undo} busy={busy} />
                    <Column bucket="cut_back" items={byBucket('cut_back')} cur={cur} savedMap={savedMap} onMark={mark} onUndo={undo} busy={busy} />
                    <Column bucket="keep" items={byBucket('keep')} cur={cur} savedMap={savedMap} onMark={mark} onUndo={undo} busy={busy} />
                  </div>

                  {snoozedItems.length > 0 && (
                    <div className="mt-5 pt-4 border-t border-slate-200" data-testid="opt-snoozed">
                      <button onClick={() => setShowSnoozed((v) => !v)} className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-500 hover:text-slate-900" data-testid="opt-snoozed-toggle">
                        <Clock className="w-3.5 h-3.5" /> Snoozed · {snoozedItems.length} <ChevronDown className={cn('w-3.5 h-3.5 transition-transform', showSnoozed && 'rotate-180')} />
                      </button>
                      {showSnoozed && (
                        <div className="mt-3 space-y-2">
                          {snoozedItems.map((it, i) => (
                            <div key={i} className="flex items-center justify-between rounded-lg bg-slate-50 border border-slate-200 px-3 py-2" data-testid={`opt-snoozed-${it.merchant}`}>
                              <div className="min-w-0"><span className="text-sm font-semibold text-slate-700">{it.merchant}</span> <span className="text-[11px] text-slate-400">· {CUR(it.monthly_saving, cur)}/mo</span></div>
                              <div className="flex items-center gap-2 shrink-0">
                                <button onClick={() => mark(it, 'actioned')} disabled={busy} className="text-[11px] font-bold text-lime-600 hover:text-lime-700">Action it</button>
                                <button onClick={() => undo(it)} disabled={busy} className="text-[11px] font-semibold text-slate-400 hover:text-slate-900">Unsnooze</button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
      </div>
    </section>
  );
};

export default ExpenseOptimiser;
